import axios from "axios";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import ViewIcon from "../../../../../icons/view";
import Trash from "../../../../../icons/trash";
import Status from "../../../../../icons/status";
import View from "./View";

type Farmacia = {
  farmacia_id: number;
  nombre: string;
  direccion: string;
  telefono: number;
  estado: string;
};

interface Props {
  counter: number;
  setCounter: React.Dispatch<React.SetStateAction<number>>;
}

const FarmaciasList = ({ counter, setCounter }: Props) => {
  const [farmacias, setFarmacias] = useState<Farmacia[]>([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteCount, setDeleteCount] = useState(0);
  const [farmaciaView, setFarmaciaView] = useState(false);
  const [farmaciaViewID, setFarmaciaViewID] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const generatePageNumbers = () => {
    return Array.from(
      { length: 5 },
      (_, index) => currentPage - 2 + index
    ).filter((page) => page >= 1 && page <= totalPages);
  };

  const handlePageChange = (page: any) => {
    setCurrentPage(page);
    getFarmacias(page);
  };

  const getFarmacias = async (page = 1) => {
    try {
      const query = `page=${page}&search=${encodeURIComponent(
        searchText
      )}&state=${encodeURIComponent(statusFilter)}`;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/farmacias?${query}`,
        {
          headers: { "auth-token": sessionStorage.getItem("token") },
        }
      );
      setCurrentPage(response.data.pageInfo.currentPage);
      setTotalPages(response.data.pageInfo.totalPages);
      setFarmacias(response.data.data);
    } catch (error) {
      if (error === "Error: Request failed with status code 404") {
        console.log("No existe el usuario");
      }
    }
  };

  const deleteFarmacia = async (farmacia_id: any) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/farmacias/${farmacia_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );

      setCounter((prevCount) => prevCount + 1);
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "Se ha eliminado la farmacia correctamente.",
        confirmButtonText: "Aceptar",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ha ocurrido un error al eliminar la farmacia.",
        confirmButtonText: "Aceptar",
      });
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getFarmacias(currentPage);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchText, statusFilter, deleteCount]);

  useEffect(() => {
    getFarmacias(currentPage);
  }, [counter, deleteCount]);

  return (
    <div className="flex flex-col overflow-hidden">
      <div>
        <input
          type="text"
          placeholder="Buscar"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="main-input"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="main-dropdown my-2"
        >
          <option value="ambos">Todos</option>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
        </select>
      </div>
      <div
        className="overflow-auto bg-white pb-8 md:pb-14"
        style={{ maxHeight: "calc(65vh)" }}
      >
        <table className="w-full grow">
          <thead className="bg-gray-50 top-0 sticky">
            <tr className="table-head">
              <th></th>
              <th className="text-left">ID</th>
              <th className="text-left">Nombre</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {farmacias.length === 0 ? (
              <tr className="hover:bg-slate-100">
                <td className="text-center" colSpan={6}>
                  No hay farmacias disponibles
                </td>
              </tr>
            ) : (
              farmacias.map((farmacia) => (
                <tr
                  className="hover:bg-slate-100 table-body"
                  key={farmacia.farmacia_id}
                >
                  <td>
                    <div className="m-auto w-fit">
                      <Status status={farmacia.estado} />
                    </div>
                  </td>
                  <td className="text-left">{farmacia.farmacia_id}</td>
                  <td>{farmacia.nombre}</td>
                  <td>
                    <div className="flex gap-2 m-auto w-fit">
                      <button
                        onClick={() => {
                          setFarmaciaView(!farmaciaView);
                          setFarmaciaViewID(farmacia.farmacia_id);
                        }}
                      >
                        <ViewIcon />
                      </button>
                      <button
                        onClick={() => {
                          Swal.fire({
                            title: "¿Estás seguro?",
                            text: "No podrás revertir esta acción",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Sí, eliminar",
                            cancelButtonText: "Cancelar",
                          }).then((result) => {
                            if (result.isConfirmed) {
                              deleteFarmacia(farmacia.farmacia_id);
                            }
                          });
                        }}
                      >
                        <Trash size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div>
        {farmacias.length > 0 && (
          <div className="flex justify-center items-center gap-4 mt-4">
            <button
              className={`${
                currentPage === 1 ? "hidden" : "cefa-button w-fit"
              }`}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Anterior
            </button>

            <div className="flex gap-2">
              {generatePageNumbers().map((page) => (
                <button
                  key={page}
                  className={`cefa-button ${
                    currentPage === page ? "active" : ""
                  }`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              className={`${
                currentPage === totalPages ? "hidden" : "cefa-button w-fit"
              }`}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
      <View
        counter={counter}
        setCounter={setCounter}
        farmaciaView={farmaciaView}
        setFarmaciaView={setFarmaciaView}
        farmaciaViewID={farmaciaViewID}
      />
    </div>
  );
};

export default FarmaciasList;
